/** The quick actions every value in the API-credential drawer offers.
 *
 *  A credential is the hinge between an operator, the person who minted it
 *  and everything authenticating with it, so most actions leave this grid:
 *  the creator opens their user page and their audit trail, the bound user
 *  opens their sessions, the fingerprint reaches the webhook subscriptions
 *  that carry it, and the last-used IP goes to WHOIS and the audit log's geo
 *  filter (the same pair the audit dialog offers).
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *  Namespaces this grid does NOT have (Phase 8): `user=` / `created_by=`, and
 *  "requests made with this key" on /api-requests — those stay unlinked
 *  rather than guessed.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  apiCredentialsHref,
  auditHref,
  cond,
  externalAccessHref,
  idCond,
  sessionsHref,
  subscriptionsHref,
} from '@/utils/gridLinks';

/** `null` tenant = a platform credential; the grid spells that `tenant=system`. */
export function tenantActions(tenant: { id: number; name?: string | null } | null): ValueAction[] {
  if (!tenant) {
    return [
      act.filter(apiCredentialsHref([cond('tenant', 'system')]), 'Platform credentials (no tenant)'),
    ];
  }
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(apiCredentialsHref([], tenant.id), 'Credentials of this operator'),
    act.filter(subscriptionsHref([], tenant.id), 'Subscriptions of this operator'),
  ];
}

/** The integration partner holding a `partner_dev` key. */
export function partnerActions(partner: { id: number }): ValueAction[] {
  return [
    act.open(`/partners/${partner.id}`, 'Open partner'),
    act.filter(apiCredentialsHref([idCond('partner', partner.id)]), 'Keys of this partner'),
    act.filter(externalAccessHref([idCond('partner', partner.id)]), 'Calls of this partner'),
  ];
}

export function scopeActions(scope: string): ValueAction[] {
  return [act.filter(apiCredentialsHref([cond('scope', scope)]), `Credentials scoped "${scope}"`)];
}

export function typeActions(credentialType: string): ValueAction[] {
  return [act.filter(apiCredentialsHref([cond('type', credentialType)]), `"${credentialType}" credentials`)];
}

export function statusActions(status: string): ValueAction[] {
  return [act.filter(apiCredentialsHref([cond('status', status)]), `Credentials "${status}"`)];
}

export function credentialIdActions(id: number): ValueAction[] {
  return [
    act.copy(String(id), 'Copy credential id'),
    act.filter(apiCredentialsHref([cond('id', String(id), 'exact')]), 'This credential in the grid'),
  ];
}

/** Who minted the key. `/api-credentials` has no `created_by=` namespace, so
 *  the provenance link is the audit log's actor filter. */
export function createdByActions(user: { id: number; email: string }): ValueAction[] {
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    act.filter(
      auditHref({ actor_email: user.email, event_contains: 'api_credential' }),
      'Credential audit by this actor',
    ),
    act.mailto(user.email),
  ];
}

/** The user a `client`-scope key acts as. */
export function boundUserActions(user: { id: number; email: string }): ValueAction[] {
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    act.open(`/users/${user.id}/sessions`, "User's sessions tab"),
    act.filter(sessionsHref({ user: user.id }), 'Sessions of this user'),
    act.mailto(user.email),
  ];
}

export function lastUsedIpActions(ip: string): ValueAction[] {
  return [act.whois(ip), act.filter(auditHref({ geo: ip }), 'Audit entries from this IP'), act.copy(ip, 'Copy IP')];
}

export function accessGroupActions(group: { id: number; name: string }): ValueAction[] {
  return [
    act.open(`/rbac/access-groups/${group.id}`, 'Open access group'),
    act.copy(group.name, 'Copy group name'),
  ];
}

export function keyPrefixActions(prefix: string): ValueAction[] {
  return [
    act.copy(prefix, 'Copy key prefix'),
    act.filter(apiCredentialsHref([cond('prefix', prefix)]), 'Credentials with this key prefix'),
  ];
}

/** The fingerprint is how a subscription names its credential. */
export function fingerprintActions(fingerprint: string, tenantId?: number | null): ValueAction[] {
  return [
    act.copy(fingerprint, 'Copy fingerprint'),
    act.filter(apiCredentialsHref([cond('fingerprint', fingerprint)]), 'Credentials with this fingerprint'),
    act.filter(
      subscriptionsHref([cond('credential', fingerprint, 'exact')], tenantId ?? null),
      'Subscriptions bound to this credential',
    ),
  ];
}

/** The "N webhook subscriptions" counter that blocks a delete. */
export function subscriptionCountActions(fingerprint: string, tenantId?: number | null): ValueAction[] {
  return [
    act.filter(
      subscriptionsHref([cond('credential', fingerprint, 'exact')], tenantId ?? null),
      'The subscriptions using this credential',
    ),
  ];
}

/** A jwt_refresh child row — not listable on its own (the grid lists roots). */
export function refreshTokenActions(child: { id: number; key_prefix: string }): ValueAction[] {
  const out: ValueAction[] = [act.copy(String(child.id), 'Copy refresh token id')];
  if (child.key_prefix) out.push(act.copy(child.key_prefix, 'Copy key prefix'));
  return out;
}
